// Live and manual figures for the Competitors tab (backend: admin/analytics/marketing/competitor-metrics).
// When the API is unreachable, the page falls back to COMPETITOR_METRICS_SEED from marketingCompetitorsData.js.
import { COMPETITOR_METRICS_SEED, DATA_CHECKED } from "./marketingCompetitorsData";

export const METRICS_URL = "admin/analytics/marketing/competitor-metrics";

export const METRIC_LABELS = {
  app_store_rating: "App Store rating",
  app_store_reviews: "App Store ratings",
  app_store_version: "App version",
  app_store_released_at: "Latest app update",
  play_installs: "Google Play installs",
  youtube_subscribers: "YouTube subscribers",
  youtube_videos: "YouTube videos",
  youtube_views: "YouTube views",
  facebook_followers: "Facebook followers",
  instagram_followers: "Instagram followers",
  tiktok_followers: "TikTok followers",
  tiktok_likes: "TikTok likes",
  x_followers: "X followers",
  linkedin_followers: "LinkedIn followers",
  trustpilot_rating: "Trustpilot rating",
  trustpilot_reviews: "Trustpilot reviews",
  forum_members: "Forum members",
  forum_posts: "Forum posts",
};

const KINDS = {
  app_store_rating: "rating",
  trustpilot_rating: "rating",
  app_store_version: "text",
  play_installs: "text",
  app_store_released_at: "date",
};

const LIVE_CHANNELS = {
  app_store_rating: "app_store",
  app_store_reviews: "app_store",
  app_store_version: "app_store",
  app_store_released_at: "app_store",
  youtube_subscribers: "youtube",
  youtube_videos: "youtube",
  youtube_views: "youtube",
};

// Display order inside each brand card.
export const METRIC_ORDER = Object.keys(METRIC_LABELS);

// Brand names used in the static tables -> competitor_key in the API.
export const BRAND_KEYS = {
  "11 Plus Magic": "ours",
  "Atom Learning": "atom",
  "Exam Happy": "exam_happy",
  Pip: "pip",
  "Eleven Ace": "eleven_ace",
  CGP: "cgp",
  "Exam Papers Plus": "exam_papers_plus",
  "Eleven Plus Exams": "eleven_plus_exams",
  PiAcademy: "piacademy",
  "Schofield & Sims": "schofield_sims",
  "11 Plus Guide": "eleven_plus_guide",
};

export const SEEDED_AT = "2026-10-30T12:00:00Z";

export function metricKind(key) {
  return KINDS[key] || "count";
}

/** Rows shaped like the API's, built from the static research (source "static", not editable). */
export function fallbackMetrics() {
  const rows = [];
  COMPETITOR_METRICS_SEED.forEach((brand, i) => {
    Object.entries(brand.metrics).forEach(([key, v]) => {
      const live = LIVE_CHANNELS[key];
      rows.push({
        id: `static-${brand.key}-${key}`,
        competitor_key: brand.key,
        competitor_name: brand.name,
        is_ours: brand.isOurs,
        sort_order: i + 1,
        metric_key: key,
        label: METRIC_LABELS[key] || key,
        kind: metricKind(key),
        value: typeof v === "number" ? v : null,
        value_text: typeof v === "string" ? v : null,
        source: "static",
        live_channel: (live === "app_store" && brand.appStoreId) || (live === "youtube" && brand.youtubeChannelId) ? live : null,
        source_url: null,
        last_checked_at: SEEDED_AT,
        last_error: null,
      });
    });
  });
  return rows;
}

/** { [competitor_key]: { key, name, isOurs, sort, metrics: { [metric_key]: row } } } */
export function indexMetrics(rows) {
  const idx = {};
  (rows || []).forEach((r) => {
    if (!idx[r.competitor_key]) {
      idx[r.competitor_key] = { key: r.competitor_key, name: r.competitor_name, isOurs: !!r.is_ours, sort: r.sort_order || 0, metrics: {} };
    }
    idx[r.competitor_key].metrics[r.metric_key] = r;
  });
  return idx;
}

export function brandList(idx) {
  return Object.values(idx || {}).sort((a, b) => (a.isOurs === b.isOurs ? a.sort - b.sort : a.isOurs ? -1 : 1));
}

export function num(idx, brand, key) {
  const v = idx?.[brand]?.metrics?.[key]?.value;
  return v === null || v === undefined || v === "" || Number.isNaN(Number(v)) ? null : Number(v);
}

function text(idx, brand, key) {
  return idx?.[brand]?.metrics?.[key]?.value_text || null;
}

// en-US for compact: en-GB writes "38.4k", the research tables use "38.4K".
export function formatCount(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return null;
  const v = Number(n);
  if (Math.abs(v) < 10000) return v.toLocaleString("en-GB", { maximumFractionDigits: 2 });
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumSignificantDigits: 3 }).format(v);
}

export function formatRating(n) {
  if (n === null || n === undefined) return null;
  return Number(n).toLocaleString("en-GB", { maximumFractionDigits: 2 });
}

export function formatDate(iso, withTime = false) {
  if (!iso) return null;
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  const opts = { day: "numeric", month: "short", year: "numeric" };
  if (withTime) Object.assign(opts, { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleString("en-GB", opts);
}

/** The value as shown on a card: "8.2K", "4.48", "10+", "Not found" or an em dash. */
export function formatMetric(row) {
  if (!row) return "—";
  const kind = row.kind || metricKind(row.metric_key);
  if (row.value !== null && row.value !== undefined && kind !== "text" && kind !== "date") {
    return kind === "rating" ? formatRating(row.value) : formatCount(row.value);
  }
  if (kind === "date" && row.value_text) return formatDate(row.value_text);
  return row.value_text || "—";
}

export function ratio(x) {
  if (!Number.isFinite(x) || x <= 0) return null;
  let r;
  if (x >= 1000) r = Math.round(x / 100) * 100;
  else if (x >= 10) r = Math.round(x);
  else r = Math.round(x * 10) / 10;
  return r.toLocaleString("en-GB");
}

function segments(s) {
  return String(s || "").split("; ");
}

const SCORECARD_MERGERS = {
  "App Store / Play Store": (idx, r) => {
    const out = {};
    const eh = num(idx, "exam_happy", "app_store_reviews");
    const ehRating = num(idx, "exam_happy", "app_store_rating");
    const ours = num(idx, "ours", "app_store_reviews");
    const play = text(idx, "ours", "play_installs");
    if (eh !== null) {
      const segs = segments(r.theirNumber);
      segs[0] = `${formatCount(eh)} iOS ratings${ehRating !== null ? ` (${formatRating(ehRating)})` : ""}`;
      out.theirNumber = segs.join("; ");
    }
    if (ours !== null) {
      const segs = segments(r.ours).map((s, i) => (i > 0 && /Play installs/.test(s) && play ? `${play} Play installs` : s));
      segs[0] = `${formatCount(ours)} rating${ours === 1 ? "" : "s"}`;
      out.ours = segs.join("; ");
    }
    if (eh !== null && ours) out.gap = `${ratio(eh / ours)}x ratings`;
    return out;
  },
  YouTube: (idx) => {
    const out = {};
    const eppViews = num(idx, "exam_papers_plus", "youtube_views");
    const atomSubs = num(idx, "atom", "youtube_subscribers");
    const atomViews = num(idx, "atom", "youtube_views");
    const subs = num(idx, "ours", "youtube_subscribers");
    const views = num(idx, "ours", "youtube_views");
    if (eppViews !== null && atomSubs !== null && atomViews !== null) {
      out.theirNumber = `EPP ${formatCount(eppViews)} lifetime views; Atom ${formatCount(atomSubs)} subs, ${formatCount(atomViews)} views`;
    }
    if (subs !== null && views !== null) out.ours = `${formatCount(subs)} subs, ${formatCount(views)} lifetime views`;
    if (atomViews !== null && views) out.gap = `Atom has about ${ratio(atomViews / views)}x the views`;
    return out;
  },
  "Facebook page + groups": (idx, r) => {
    const out = {};
    const atom = num(idx, "atom", "facebook_followers");
    const ours = num(idx, "ours", "facebook_followers");
    if (atom !== null) {
      const segs = segments(r.theirNumber);
      segs[0] = `${formatCount(atom)} page followers`;
      out.theirNumber = segs.join("; ");
    }
    if (ours !== null) out.ours = `${formatCount(ours)} followers on the EdTech-Ltd company page`;
    if (atom !== null && ours) out.gap = `About ${ratio(atom / ours)}x`;
    return out;
  },
  TikTok: (idx) => followerRow(idx, "tiktok_followers"),
  Instagram: (idx) => followerRow(idx, "instagram_followers"),
};

function followerRow(idx, key) {
  const out = {};
  const cgp = num(idx, "cgp", key);
  const atom = num(idx, "atom", key);
  const ours = num(idx, "ours", key);
  if (cgp !== null && atom !== null) out.theirNumber = `${formatCount(cgp)}; ${formatCount(atom)} followers`;
  if (ours !== null) out.ours = `${formatCount(ours)} followers`;
  if (atom !== null && ours) out.gap = `About ${ratio(atom / ours)}x vs Atom`;
  return out;
}

/** Scorecard rows with numbers from the live/manual figures (strategy text stays as written). */
export function mergeScorecard(scorecard, idx) {
  if (!idx) return scorecard;
  return scorecard.map((r) => {
    const merge = SCORECARD_MERGERS[r.channel];
    if (!merge) return r;
    const patch = merge(idx, r);
    return Object.keys(patch).length ? { ...r, ...patch } : r;
  });
}

const LEADING_NUMBER = /^\d[\d.,]*[KM]?/;

function replaceLeadingNumber(cell, value) {
  return LEADING_NUMBER.test(cell) ? cell.replace(LEADING_NUMBER, value) : value;
}

/** "Followers by platform" rows (Brand, Facebook, Instagram, YouTube, TikTok, X, Other). */
export function mergeMarketLeaderRows(rows, idx) {
  if (!idx) return rows;
  return rows.map((row) => {
    const key = BRAND_KEYS[row[0]];
    if (!key || !idx[key]) return row;
    const out = [...row];
    [[1, "facebook_followers"], [2, "instagram_followers"], [5, "x_followers"]].forEach(([col, metric]) => {
      const v = num(idx, key, metric);
      if (v !== null) out[col] = replaceLeadingNumber(row[col], formatCount(v));
    });
    const subs = num(idx, key, "youtube_subscribers");
    const videos = num(idx, key, "youtube_videos");
    if (subs !== null) out[3] = `${formatCount(subs)} subs${videos !== null ? `, ${formatCount(videos)} videos` : ""}`;
    const tiktok = num(idx, key, "tiktok_followers");
    const likes = num(idx, key, "tiktok_likes");
    if (tiktok !== null) out[4] = `${formatCount(tiktok)} followers${likes !== null ? `, ${formatCount(likes)} likes` : ""}`;
    return out;
  });
}

export const STATIC_NOTE = `Live figures unavailable, showing the saved research (${DATA_CHECKED.replace(/^Data checked /, "")}).`;

/** Snackbar text for a finished refresh. */
export function refreshSummary(refresh, message) {
  if (!refresh) return { severity: "success", text: message || "Live figures refreshed." };
  const errors = refresh.errors || [];
  const warnings = refresh.warnings || [];
  if (!errors.length && !warnings.length) return { severity: "success", text: message || "Live figures refreshed." };
  const parts = [];
  if (errors.length) {
    const names = errors.slice(0, 3).map((e) => `${e.competitor_name} (${e.channel === "youtube" ? "YouTube" : "App Store"})`);
    parts.push(`Refreshed with ${errors.length} ${errors.length === 1 ? "problem" : "problems"}: ${names.join(", ")}${errors.length > 3 ? "…" : ""}.`);
  } else {
    parts.push("Live figures refreshed.");
  }
  warnings.forEach((w) => parts.push(`${w}.`));
  return { severity: "warning", text: parts.join(" ") };
}
